import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { channels, deliveryStatuses } from "../../shared/constants";
import { BotIcon, UserIcon, Clock, Bell, SendIcon, XIcon, Trash2, ChevronLeft, ChevronRight, Users, Volume2, Paperclip } from "lucide-react";
import AudioPlayer from "../../components/AudioPlayer";
import MediaSelectionModal from "../../components/MediaSelectionModal";
import { APP_NAME } from "../../config";

import { toast } from "react-toastify";
import { format, isValid, fromUnixTime, isToday, isYesterday, differenceInDays, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
/**
 * @typedef {import('../types/chat').MediaInfo} MediaInfo
 * @typedef {import('../types/chat').MediaMessageProps} MediaMessageProps
 * @typedef {import('../types/chat').MessageKey} MessageKey
 * @typedef {import('../types/chat').MediaContent} MediaContent
 * @typedef {import('../types/chat').MessageContent} MessageContent
 * @typedef {import('../types/chat').Message} Message
 * @typedef {import('../types/chat').ChatInfo} ChatInfo
 */

const { ipcRenderer } = window.require("electron");

/**
 * Componente para renderizar mensagens de mídia com ícones e estilo apropriados
 * @param {MediaMessageProps} props - Propriedades do componente
 * @returns {JSX.Element} Elemento React renderizado
 */
const MediaMessage = ({ type, caption }) => {
	const getMediaInfo = () => {
		switch (type) {
			case "image":
				return {
					icon: "🖼️",
					label: "Imagem",
					bgColor: "bg-statusInfo/20",
					borderColor: "border-statusInfo/30",
				};
			case "video":
				return {
					icon: "🎥",
					label: "Vídeo",
					bgColor: "bg-statusError/20",
					borderColor: "border-statusError/30",
				};
			case "audio":
				return {
					icon: "🎵",
					label: "Áudio",
					bgColor: "bg-statusWarning/20",
					borderColor: "border-statusWarning/30",
				};
			case "document":
				return {
					icon: "📄",
					label: "Documento",
					bgColor: "bg-statusYellow/20",
					borderColor: "border-statusYellow/30",
				};
			case "sticker":
				return {
					icon: "🏷️",
					label: "Sticker",
					bgColor: "bg-statusSuccess/20",
					borderColor: "border-statusSuccess/30",
				};
			case "contact":
				return {
					icon: "👤",
					label: "Contato",
					bgColor: "bg-statusBrand/20",
					borderColor: "border-statusBrand/30",
				};
			case "location":
				return {
					icon: "📍",
					label: "Localização",
					bgColor: "bg-statusError/20",
					borderColor: "border-statusError/30",
				};
			case "reaction":
				return {
					icon: "👍",
					label: "Reação",
					bgColor: "bg-statusYellow/20",
					borderColor: "border-statusYellow/30",
				};
			case "interactive":
				return {
					icon: "🔘",
					label: "Mensagem Interativa",
					bgColor: "bg-statusInfo/20",
					borderColor: "border-statusInfo/30",
				};
			case "poll":
				return {
					icon: "📊",
					label: "Enquete",
					bgColor: "bg-statusInteractive/20",
					borderColor: "border-statusInteractive/30",
				};
			default:
				return {
					icon: "💬",
					label: "Mensagem não suportada",
					bgColor: "bg-statusMuted/20",
					borderColor: "border-statusMuted/30",
				};
		}
	};

	const { icon, label, bgColor, borderColor } = getMediaInfo();

	return (
		<div
			className={`flex items-center p-2 rounded-md ${bgColor} border ${borderColor}`}
		>
			<span className="text-2xl mr-2">{icon}</span>
			<div className="flex flex-col">
				<span className="font-medium">{label}</span>
				{caption && <span className="text-xs mt-1 opacity-80">{caption}</span>}
			</div>
		</div>
	);
};

/**
 * Componente para mostrar um alerta de conexão
 * @param {Object} props - Propriedades do componente
 * @param {boolean} props.connected - Status de conexão
 * @param {boolean} props.isMediaModalOpen - Estado do modal de mídia
 * @param {Function} props.setIsMediaModalOpen - Função para alterar estado do modal
 * @param {Object} props.selectedChat - Chat selecionado
 * @returns {JSX.Element|null} Elemento React ou null se conectado
 */
const ConnectionAlert = ({ connected, isMediaModalOpen, setIsMediaModalOpen, selectedChat }) => {
	if (connected) return null;

	return (
		<div className="w-full px-4 py-3 bg-statusError/80 text-white shadow-md">
			<div className="flex items-center">
				<div className="flex-shrink-0">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="h-5 w-5"
						viewBox="0 0 20 20"
						fill="currentColor"
						aria-hidden="true"
					>
						<path
							fillRule="evenodd"
							d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
							clipRule="evenodd"
						/>
					</svg>
				</div>
				<div className="ml-3">
					<p className="font-medium">WhatsApp desconectado</p>
					<p className="text-sm opacity-90">
						Conecte-se ao WhatsApp para visualizar e receber suas mensagens.
					</p>
				</div>
			</div>

		</div>
	);
};

/**
 * Componente para reproduzir mensagens de áudio com carregamento via IPC
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.message - Mensagem contendo o áudio
 * @param {string} props.instanceId - ID da instância
 * @param {Object} props.selectedChat - Chat selecionado
 * @param {boolean} props.isFromMe - Se a mensagem é minha
 * @param {boolean} props.isAI - Se a mensagem é da IA
 * @returns {JSX.Element} Componente de player de áudio
 */
const AudioMessagePlayer = ({ message, instanceId, selectedChat, isFromMe, isAI }) => {
	const [audioData, setAudioData] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const loadAudioData = async () => {
			if (!message?.key?.id || !selectedChat?.id) {
				setError("Dados de mensagem inválidos");
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				setError(null);

				const result = await ipcRenderer.invoke('get-message-audio', {
					instanceId,
					chatId: selectedChat.id,
					messageId: message.key.id
				});

				if (result.success) {
					setAudioData(result);
				} else {
					setError(result.error || "Erro ao carregar áudio");
				}
			} catch (err) {
				console.error("Erro ao carregar dados de áudio:", err);
				setError("Erro ao carregar áudio");
			} finally {
				setIsLoading(false);
			}
		};

		loadAudioData();
	}, [message?.key?.id, selectedChat?.id, instanceId]);

	if (isLoading) {
		return (
			<div className="flex items-center p-2.5 rounded-xl bg-statusWarning/10 border border-statusWarning/60 space-x-3">
				<div className="flex items-center justify-center w-12 h-12 rounded-full bg-statusWarning/20 flex-shrink-0">
					<div className="w-4 h-4 border-2 border-statusWarning border-t-transparent rounded-full animate-spin" />
				</div>
				<div className="flex-1 min-w-0">
					<span className="text-sm font-medium text-statusWarning">Carregando áudio...</span>
				</div>
			</div>
		);
	}

	if (error || !audioData) {
		return (
			<div className="flex items-center p-2.5 rounded-xl bg-redBase/10 border border-redBase/60 space-x-3">
				<div className="flex items-center justify-center w-12 h-12 rounded-full bg-redBase/20 flex-shrink-0">
					<Volume2 className="h-5 w-5 text-redBase" />
				</div>
				<div className="flex-1 min-w-0">
					<span className="text-sm font-medium text-redBase">Erro no áudio</span>
					<div className="text-xs text-redBase/70">{error || "Não foi possível carregar o áudio"}</div>
				</div>
			</div>
		);
	}

	return (
		<AudioPlayer
			audioUrl={audioData.audioData}
			caption={audioData.caption}
			isFromMe={isFromMe}
			isAI={isAI}
			duration={audioData.duration}
		/>
	);
};

/**
 * Componente para mostrar um toggle de modo IA em cada chat (Controlled Component)
 * @param {Object} props - Propriedades do componente
 * @param {string} props.chatId - ID do chat
 * @param {string} props.instanceId - ID da instância
 * @param {Object} props.aiModeStatus - Status do modo IA vindo do estado global
 * @param {Function} props.onStatusChange - Função para notificar mudanças de status
 * @returns {JSX.Element} Elemento React
 */
const AIModeToggle = ({ chatId, instanceId, aiModeStatus, onStatusChange }) => {
	const [isToggling, setIsToggling] = useState(false);

	const handleToggle = async (e) => {
		e.stopPropagation(); // Evita que o clique selecione o chat

		if (isToggling) return;

		// Se não pode alterar, mostra toast explicativo
		if (!aiModeStatus?.canToggle) {
			const toastId = `toggle-restriction-${chatId}`;
			if (!toast.isActive(toastId)) {
				toast.info(aiModeStatus.reason, {
					position: "top-right",
					autoClose: 6000,
					toastId: toastId
				});
			}
			return;
		}

		setIsToggling(true);

		try {
			const result = await ipcRenderer.invoke("set-ai-mode", {
				instanceId,
				chatId,
				active: !aiModeStatus.active
			});

			if (result.success) {
				// Notifica o componente pai para atualizar o estado global
				onStatusChange?.(chatId, result.status);
			} else {
				// Mostra toast explicativo sobre por que não pode alterar
				toast.info(result.message, {
					position: "top-right",
					autoClose: 6000
				});
			}
		} catch (error) {
			console.error("Erro ao alternar modo IA:", error);
			toast.error("Erro ao alterar modo IA");
		} finally {
			setIsToggling(false);
		}
	};

	if (!aiModeStatus) {
		return (
			<div className="ml-2 flex items-center">
				<div className="h-6 w-12 bg-emeraldDeep/40 rounded-full animate-pulse" />
			</div>
		);
	}

	const isGroup = aiModeStatus.isGroup;
	const canToggle = aiModeStatus.canToggle;
	const isActive = aiModeStatus.active;

	return (
		<div className="ml-2 flex items-center" title={aiModeStatus.reason}>
			{/* Switch Estilizado */}
			<button
				type="button"
				onClick={handleToggle}
				disabled={isToggling}
				className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${isToggling ? "opacity-50" : ""
					} ${!canToggle
						? "bg-statusMutedDark cursor-not-allowed"
						: isActive
							? "bg-primaryDark"
							: "bg-statusBrandDark"
					}`}
			>
				{/* Ícone do Bot (Modo IA) - À esquerda */}
				<span
					className={`absolute left-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full transition-all text-white ${isActive ? "opacity-100" : "opacity-40"
						}`}
				>
					<BotIcon size={12} />
				</span>

				{/* Bolinha do Switch */}
				<span
					className={`absolute top-0.5 flex h-5 w-5 transform items-center justify-center rounded-full bg-white transition-transform ${isActive ? "translate-x-6" : "translate-x-0.5"
						} ${isToggling ? "animate-pulse" : ""}`}
				>
					{isToggling && (
						<div
							className={`h-3 w-3 border-2 border-current border-t-transparent rounded-full animate-spin ${isActive ? "text-emeraldPrimary" : "text-purplePrimary"}`}
						/>
					)}
				</span>

				{/* Ícone do Usuário (Modo Manual) - À direita */}
				<span
					className={`absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full transition-all text-white ${!isActive ? "opacity-100" : "opacity-40"
						}`}
				>
					<UserIcon size={12} />
				</span>
			</button>



			{/* Indicador especial para grupos */}
			{isGroup && (
				<span className="ml-1 text-xs text-primaryLight/60" title="IA desativada para grupos">
					👥
				</span>
			)}
		</div>
	);
};

/**
 * Componente para exibir o timer de envio de follow-up (com suporte a múltiplas mensagens)
 * @param {Object} props - Propriedades do componente
 * @param {any[]} props.followUps - Array de follow-ups agendados
 * @param {Function} props.formatTime - Função para formatar o tempo
 * @returns {JSX.Element} Componente React renderizado
 */
const FollowUpSendTimer = ({ followUps, formatTime }) => {
	if (!followUps || followUps.length === 0) return null;

	// Ordena os follow-ups por tempo de envio
	const sortedFollowUps = [...followUps].sort((a, b) => a.scheduledTime - b.scheduledTime);
	const nextFollowUp = sortedFollowUps[0];
	const [timeLeft, setTimeLeft] = useState(nextFollowUp.scheduledTime - Date.now());
	const scheduledTimeRef = useRef(nextFollowUp.scheduledTime);

	useEffect(() => {
		// Atualiza a referência quando o scheduledTime mudar
		scheduledTimeRef.current = nextFollowUp.scheduledTime;

		// Atualiza o tempo inicial
		setTimeLeft(nextFollowUp.scheduledTime - Date.now());

		// Atualiza a cada segundo
		const timer = setInterval(() => {
			const newTimeLeft = scheduledTimeRef.current - Date.now();
			setTimeLeft(newTimeLeft);

			// Se o tempo acabou, limpa o timer
			if (newTimeLeft <= 0) {
				clearInterval(timer);
			}
		}, 1000);

		// Limpa o timer ao desmontar
		return () => clearInterval(timer);
	}, [nextFollowUp.scheduledTime]);

	return (
		<div className="flex flex-col gap-2">
			{/* Timer da próxima mensagem */}
			<div className="flex items-center bg-statusYellowDarkest/40 px-3 py-1 rounded-md border border-statusYellowDark/30">
				<Clock size={14} className="text-statusYellowLight mr-2" />
				<span className="text-sm text-statusYellowLight">
					{timeLeft <= 0 ? "Enviando em breve..." : `Próxima em ${formatTime(timeLeft)}`}
				</span>
				{sortedFollowUps.length > 1 && (
					<span className="ml-2 text-xs text-statusYellowLight font-medium">
						({nextFollowUp.sequenceIndex || 1}/{nextFollowUp.totalInSequence || sortedFollowUps.length})
					</span>
				)}
			</div>

			{/* Lista de todas as mensagens agendadas */}
			{sortedFollowUps.length > 1 && (
				<div className="text-xs text-statusYellowLight bg-statusYellowDarkest/20 px-2 py-1 rounded border border-statusYellowDark/20">
					<div className="font-medium mb-1">Sequência completa:</div>
					{sortedFollowUps.map((followUp, index) => {
						const delay = followUp.scheduledTime - Date.now();
						const isNext = index === 0;
						return (
							<div key={followUp.scheduledTime} className={`flex justify-between ${isNext ? "font-medium" : "opacity-75"}`}>
								<span>Msg {index + 1}:</span>
								<span>
									{delay <= 0 ? "Enviando..." : formatTime(delay)}
								</span>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

/**
 * Componente principal de chat que gerencia conversas e mensagens
 * @returns {JSX.Element} Componente React renderizado
 */
export function Chat({ instanceId }) {
	/** @type {[ChatInfo[], React.Dispatch<React.SetStateAction<ChatInfo[]>>]} */
	const [chats, setChats] = useState([]);

	/** @type {[ChatInfo|null, React.Dispatch<React.SetStateAction<ChatInfo|null>>]} */
	const [selectedChat, setSelectedChat] = useState(null);

	/** @type {[Message[], React.Dispatch<React.SetStateAction<Message[]>>]} */
	const [messages, setMessages] = useState([]);

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [showStats, setShowStats] = useState(false);

	/** @type {[ConnectionStatus, React.Dispatch<React.SetStateAction<ConnectionStatus>>]} */
	const [connectionStatus, setConnectionStatus] = useState({
		connected: false,
		lastUpdate: 0,
		reason: "Iniciando aplicação...",
		reconnectAttempts: 0,
	});

	/** @type {[string[], React.Dispatch<React.SetStateAction<string[]>>]} */
	const [newMessageIds, setNewMessageIds] = useState([]);

	/** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
	const [messageInput, setMessageInput] = useState("");

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [isSending, setIsSending] = useState(false);

	/** @type {[Object, React.Dispatch<React.SetStateAction<Object>>]} */
	const [allInterventions, setAllInterventions] = useState({});

	/** @type {[Object, React.Dispatch<React.SetStateAction<Object>>]} */
	const [activeFollowUps, setActiveFollowUps] = useState({});

	/** @type {[Object, React.Dispatch<React.SetStateAction<Object>>]} */
	const [followUpChecks, setFollowUpChecks] = useState({});

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [isCheckingFollowUp, setIsCheckingFollowUp] = useState(false);

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [isClearingChat, setIsClearingChat] = useState(false);

	/** @type {[Object, React.Dispatch<React.SetStateAction<Object>>]} */
	const [aiModeStatuses, setAiModeStatuses] = useState({});

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
	const [alertNumber, setAlertNumber] = useState("");
	const [alertEnabledLocal, setAlertEnabledLocal] = useState(true);
	const [isSavingAlertSettings, setIsSavingAlertSettings] = useState(false);
	const [isSendingTestAlert, setIsSendingTestAlert] = useState(false);

	/** @type {React.RefObject<number>} */
	const interventionTimerRef = useRef(null);

	/** @type {React.RefObject<HTMLDivElement>} */
	const messagesEndRef = useRef(null);

	/** @type {React.RefObject<HTMLTextAreaElement>} */
	const messageInputRef = useRef(null);

	/** @type {React.RefObject<string|null>} */
	const prevInstanceIdRef = useRef(null);

	// Memoização estável das condições de follow-up para evitar re-renders
	const followUpConditions = useMemo(() => {
		if (!selectedChat) {
			return {
				shouldShowBlueSection: false,
				shouldShowYellowSection: false,
				hasFollowUp: false
			};
		}

		const chatId = selectedChat.id;
		const aiModeStatus = aiModeStatuses[chatId];
		const followUps = activeFollowUps[chatId] || [];
		const hasFollowUp = followUps.length > 0;

		const shouldShowBlueSection = aiModeStatus?.active && !hasFollowUp;
		const shouldShowYellowSection = aiModeStatus?.active && hasFollowUp;

		return {
			shouldShowBlueSection,
			shouldShowYellowSection,
			hasFollowUp
		};
	}, [selectedChat?.id, aiModeStatuses, activeFollowUps]);

	// Estado derivado para o botão espelho no header
	const headerButtonState = useMemo(() => {
		if (!selectedChat) return null;
		
		const chatId = selectedChat.id;
		const aiModeStatus = aiModeStatuses[chatId];
		const intervention = allInterventions[chatId];
		
		return {
			// Se há intervenção, mostra como modo humano
			isHumanMode: Boolean(intervention),
			// Modo efetivo considerando IA + intervenção
			effectiveHumanMode: Boolean(intervention) || !aiModeStatus?.active,
			// Razão do estado atual
			reason: intervention 
				? (intervention.isManualIntervention ? "Modo manual ativo" : "Intervenção humana ativa")
				: aiModeStatus?.active 
					? "Modo IA ativo" 
					: (aiModeStatus?.reason || "IA desativada"),
			// Se pode ser alterado via AIModeToggle
			canToggle: aiModeStatus?.canToggle ?? true
		};
	}, [selectedChat?.id, aiModeStatuses, allInterventions]);

	/**
	 * Função para rolar para o final das mensagens
	 */
	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	// Rola para o final quando as mensagens forem atualizadas
	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	/**
	 * Calcula estatísticas simples de tempo e mensagens para o chat atual
	 * @param {Message[]} msgs
	 */
	const computeChatStats = useCallback((msgs) => {
		if (!Array.isArray(msgs) || msgs.length === 0) return { messageCount: 0, avgResponseMs: null };

		let messageCount = msgs.length;
		let totalResponse = 0;
		let responsePairs = 0;

		for (let i = 0; i < msgs.length; i++) {
			const m = msgs[i];
			if (!m || !m.key) continue;
			const fromUser = !m.key.fromMe;
			if (!fromUser) continue;
			// find next bot message
			for (let j = i + 1; j < msgs.length; j++) {
				const nxt = msgs[j];
				if (!nxt || !nxt.key) continue;
				if (nxt.key.fromMe) {
					const diff = (nxt.messageTimestamp || nxt.timestamp || 0) - (m.messageTimestamp || m.timestamp || 0);
					if (diff > 0) {
						totalResponse += diff;
						responsePairs++;
					}
					break;
				}
			}
		}

		const avgResponseMs = responsePairs > 0 ? Math.round(totalResponse / responsePairs) : null;
		return { messageCount, avgResponseMs, responsePairs };
	}, []);



	// Limpa os IDs de novas mensagens após 2 segundos
	useEffect(() => {
		if (newMessageIds.length > 0) {
			const timer = setTimeout(() => {
				setNewMessageIds([]);
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [newMessageIds]);

	// Carrega a lista de chats para a instância selecionada
	useEffect(() => {
		if (!instanceId) return;

		// Limpa os estados relacionados ao chat quando mudamos de instância

		// Somente limpa o chat selecionado quando a instância realmente mudar
		if (prevInstanceIdRef.current && prevInstanceIdRef.current !== instanceId) {
			setSelectedChat(null);
			setMessages([]);
			setAiModeStatuses({}); // Limpa status IA ao mudar instância
		}

		// Atualiza a referência para a instância atual
		prevInstanceIdRef.current = instanceId;

		const loadChats = () => {
			ipcRenderer
				.invoke("get-chats", instanceId)
				.then(async (chatsInfo) => {
					const filteredChats = chatsInfo.filter((chat) => {
						const hasValidMessage =
							chat.lastMessage && chat.lastMessage.trim() !== "";
						const hasValidTimestamp = chat.timestamp && chat.timestamp > 0;
						return hasValidMessage && hasValidTimestamp;
					});
					setChats(filteredChats);
					
					// Carrega status IA para todos os chats
					if (filteredChats.length > 0) {
						try {
							const chatIds = filteredChats.map(chat => chat.id);
							const aiStatusResult = await ipcRenderer.invoke('get-ai-mode-bulk', {
								instanceId,
								chatIds
							});
							
							if (aiStatusResult.success) {
								setAiModeStatuses(aiStatusResult.results);
							}
						} catch (error) {
							console.error("Erro ao carregar status IA dos chats:", error);
						}
					}
				})
				.catch((error) => {
					console.error("Erro ao obter chats:", error);
				});
		};

		// Carrega os follow-ups ativos
		const loadActiveFollowUps = () => {
			ipcRenderer
				.invoke("get-active-follow-ups", instanceId)
				.then((followUps) => {
					setActiveFollowUps(followUps || {});
				})
				.catch((error) => {
					console.error("Erro ao obter follow-ups ativos:", error);
				});
		};

		loadChats();
		loadActiveFollowUps();

		// carrega configurações de alerta quando a aba for aberta
		if (showStats) {
			ipcRenderer.invoke('load-settings', instanceId).then((s) => {
				if (s) {
					setAlertNumber(s.ALERT_NUMBER || "");
					setAlertEnabledLocal(typeof s.ALERT_ENABLED === 'boolean' ? s.ALERT_ENABLED : true);
				}
			}).catch((err)=>console.error('Erro ao carregar settings para alert UI', err));
		}

		// Também carrega a lista de intervenções humanas ativas
		ipcRenderer
			.invoke("get-all-human-interventions")
			.then((interventions) => {
				setAllInterventions(interventions || {});
			})
			.catch((error) => {
				console.error("Erro ao obter lista de intervenções humanas:", error);
			});

		// Configura timer para atualizar os tempos restantes a cada 30s
		interventionTimerRef.current = setInterval(() => {
			ipcRenderer
				.invoke("get-all-human-interventions")
				.then((interventions) => {
					setAllInterventions(interventions || {});
				})
				.catch((error) => {
					console.error("Erro ao atualizar lista de intervenções:", error);
				});

			// Também atualiza os follow-ups ativos
			loadActiveFollowUps();
		}, 30000);

		/**
		 * Listener para novas mensagens
		 * @param {Event} _ - Evento
		 * @param {Object} data - Dados da mensagem
		 * @param {string} data.instanceId - ID da instância
		 * @param {string} data.chatId - ID do chat
		 * @param {Message} data.message - Conteúdo da mensagem
		 */
		const newMessageListener = (
			_,
			{ instanceId: msgInstanceId, chatId, message },
		) => {

			if (msgInstanceId === instanceId) {
				// Atualiza a lista de chats quando uma nova mensagem chegar
				loadChats();

				// Atualiza a lista de follow-ups ativos
				loadActiveFollowUps();

				// Atualiza a lista de intervenções
				ipcRenderer
					.invoke("get-all-human-interventions")
					.then((interventions) => {
						setAllInterventions(interventions || {});
					})
					.catch((error) => {
						console.error("Erro ao atualizar lista de intervenções:", error);
					});

				// Se o chat atual for o que recebeu a mensagem, atualiza as mensagens E as informações de follow-up check
				if (selectedChat && selectedChat.id === chatId) {

					ipcRenderer
						.invoke("get-chat-messages", { instanceId, chatId })
						.then((chatMessages) => {
							setMessages(chatMessages);

							// Adiciona o ID da nova mensagem para destaque
							if (message?.key?.id) {
								setNewMessageIds((prev) => [...prev, message.key.id]);
							}
						})
						.catch((error) => {
							console.error("Erro ao atualizar mensagens:", error);
						});

					// Atualiza informações de follow-up check para o chat atual
					ipcRenderer
						.invoke("get-follow-up-check-info", { instanceId, chatId })
						.then((info) => {
							if (info.hasScheduledCheck) {
								setFollowUpChecks(prev => ({
									...prev,
									[chatId]: info.checkTime
								}));
							} else {
								setFollowUpChecks(prev => {
									const newChecks = { ...prev };
									delete newChecks[chatId];
									return newChecks;
								});
								setFollowUpCheckTimeLeft(0);
							}
						})
						.catch((error) => {
							console.error("Erro ao atualizar informações de follow-up check:", error);
						});
				}
			}
		};

		/**
		 * Listener para resultados de verificação automática de follow-up
		 * (Este listener é responsável por toasts de verificações automáticas da IA)
		 * @param {Event} _ - Evento
		 * @param {Object} data - Dados do resultado da verificação
		 */
		const followUpCheckResultListener = (
			_,
			{ instanceId: checkInstanceId, chatId, success, hasFollowUp, message, reason, isAutomaticCheck }
		) => {

			if (checkInstanceId !== instanceId) {
				return;
			}

			// ✅ SEMPRE atualiza a lista de follow-ups após qualquer verificação
			loadActiveFollowUps();

			// ✅ CORREÇÃO: Só exibe toasts se NÃO for verificação automática
			if (!isAutomaticCheck && success) {
				// Usar IDs únicos específicos para verificações manuais
				const baseToastId = `manual-follow-up-${chatId}`;

				if (hasFollowUp) {
					// Toast de sucesso para follow-up agendado manualmente
					if (!toast.isActive(`${baseToastId}-success`)) {
						toast.success(message, {
							position: "top-right",
							autoClose: 5000,
							toastId: `${baseToastId}-success`
						});
					}
				} else {
					// Toast informativo para quando não há follow-up necessário
					if (!toast.isActive(`${baseToastId}-info`)) {
						toast.info(message, {
							position: "top-right",
							autoClose: 5000,
							toastId: `${baseToastId}-info`
						});
					}
				}

				// Toast adicional com razão da IA (apenas se tiver razão)
				if (reason && !toast.isActive(`${baseToastId}-reason`)) {
					toast.info(reason, {
						position: "top-right",
						autoClose: 8000,
						delay: 1000,
						toastId: `${baseToastId}-reason`
					});
				}
			}
		};

		// Primeiro remova quaisquer listeners antigos para evitar duplicação
		ipcRenderer.removeListener(channels.NEW_MESSAGE, newMessageListener);

		// Agora registre os listeners
		ipcRenderer.on(channels.NEW_MESSAGE, newMessageListener);
		// Registra o listener para resultados de verificação de follow-up
		ipcRenderer.on('follow-up-check-result', followUpCheckResultListener);

		return () => {
			ipcRenderer.removeListener(channels.NEW_MESSAGE, newMessageListener);
			ipcRenderer.removeListener('follow-up-check-result', followUpCheckResultListener);
			// Limpa o timer ao desmontar
			if (interventionTimerRef.current) {
				clearInterval(interventionTimerRef.current);
			}
		};
	}, [instanceId, selectedChat]);

	// Verificar o estado de intervenção humana para o chat selecionado
	useEffect(() => {
		if (!selectedChat || !instanceId) return;

		// Consulta os detalhes da intervenção humana para o chat
		ipcRenderer
			.invoke("get-human-intervention-details", {
				chatId: selectedChat.id,
			})
			.then((details) => {
				// Atualiza o estado global de intervenções
				setAllInterventions((prev) => {
					const newInterventions = { ...prev };

					if (details) {
						// Adicionando nova intervenção
						newInterventions[selectedChat.id] = details;
					} else {
						// Removendo intervenção
						delete newInterventions[selectedChat.id];
					}

					return newInterventions;
				});
			})
			.catch((error) => {
				console.error("Erro ao obter detalhes de intervenção humana:", error);
			});
	}, [selectedChat, instanceId]);

	// Carrega as mensagens para o chat selecionado
	useEffect(() => {
		if (!selectedChat || !instanceId) return;

		ipcRenderer
			.invoke("get-chat-messages", { instanceId, chatId: selectedChat.id })
			.then((chatMessages) => {
				setMessages(chatMessages);
				setNewMessageIds([]); // Limpa os destaques ao mudar de chat
			})
			.catch((error) => {
				console.error("Erro ao obter mensagens:", error);
			});
	}, [selectedChat, instanceId]);

	// Carrega o status de conexão e monitora mudanças
	useEffect(() => {
		if (!instanceId) return;


		ipcRenderer
			.invoke("get-connection-status", instanceId)
			.then((status) => {
				setConnectionStatus(prev => ({
					...prev,
					connected: status.connected,
					lastUpdate: Date.now()
				}));
			})
			.catch((error) => {
				console.error("Erro ao obter status de conexão:", error);
				setConnectionStatus((prev) => ({
					...prev,
					connected: false,
					lastUpdate: Date.now()
				}));
			});

		const statusUpdateListener = (
			_,
			{ status, instanceId: msgInstanceId, reason },
		) => {
			if (instanceId !== msgInstanceId) return;

			const isConnected = status === "open";
			const isDisconnectedByValidation =
				status === "disconnected-by-validation";
			const isDisconnectedByError = status === "disconnected-by-error";

			setConnectionStatus((prev) => ({
				...prev,
				connected: isConnected,
				lastUpdate: Date.now()
			}));

			if (isConnected) {
				toast.success("WhatsApp conectado com sucesso!");
			}

			if (isDisconnectedByValidation || isDisconnectedByError) {
				console.log(
					`[CHAT UI] Desconexão detectada para instância ${instanceId}: ${reason}`,
				);
				toast.error(`WhatsApp desconectado: ${reason || "Erro de validação"}`, {
					autoClose: false,
				});

				setConnectionStatus((prev) => ({
					...prev,
					connected: false,
					lastUpdate: Date.now()
				}));
			}
		};

		ipcRenderer.on(channels.STATUS_UPDATE, statusUpdateListener);

		return () => {
			ipcRenderer.removeListener(channels.STATUS_UPDATE, statusUpdateListener);
		};
	}, [instanceId]);

	// Função handleInterventionToggle removida - botão header agora é funcional
	
	/**
	 * Força atualização de todos os estados relacionados ao modo IA
	 * Usado para garantir sincronização entre header e sidebar
	 */
	const forceRefreshAIStates = useCallback(async (specificChatId = null) => {
		if (!instanceId) return;
		
		try {
			const chatIds = specificChatId ? [specificChatId] : chats.map(chat => chat.id);
			if (chatIds.length === 0) return;
			
			const result = await ipcRenderer.invoke('get-ai-mode-bulk', {
				instanceId,
				chatIds
			});
			
			if (result.success) {
				setAiModeStatuses(prev => ({
					...prev,
					...result.results
				}));
			}
		} catch (error) {
			console.error("Erro ao forçar atualização dos estados IA:", error);
		}
	}, [instanceId, chats]);

	/**
	 */
	const handleAIModeStatusChange = useCallback((chatId, status) => {
		setAiModeStatuses(prev => ({
			...prev,
			[chatId]: status
		}));
	}, []);

	// Carrega o status do modo IA para o chat selecionado
	useEffect(() => {
		if (!selectedChat || !instanceId) return;

		const loadAIModeStatus = async () => {
			try {
				const result = await ipcRenderer.invoke("get-ai-mode-status", {
					instanceId,
					chatId: selectedChat.id
				});

				if (result.success) {
					setAiModeStatuses(prev => ({
						...prev,
						[selectedChat.id]: result.status
					}));
				}
			} catch (error) {
				console.error("Erro ao carregar status do modo IA:", error);
			}
		};

		loadAIModeStatus();
	}, [selectedChat?.id, instanceId]);

	/**
	 * Formata timestamp usando date-fns para melhor compatibilidade
	 * @param {number|string} timestamp - Timestamp da mensagem
	 * @returns {string} Horário formatado
	 */
	const formatMessageDate = (timestamp) => {
		if (!timestamp) return "";

		try {
			let date;
		const timestampNum = Number(timestamp);

			if (Number.isNaN(timestampNum) || timestampNum <= 0) {
				return "";
			}

			// Se o timestamp está em segundos (< 10 bilhões), converte para milissegundos
			if (timestampNum < 10000000000) {
				date = fromUnixTime(timestampNum);
			} else {
				// Timestamp já está em milissegundos
				date = new Date(timestampNum);
			}

			// Verifica se a data é válida
			if (!isValid(date)) {
				return "";
			}

			// Formata usando date-fns
			return format(date, "HH:mm", { locale: ptBR });
		} catch (error) {
			console.error("Erro ao formatar timestamp:", error, "timestamp:", timestamp);
			return "";
		}
	};

	/**
	 * @param {number} timestamp - Timestamp da mensagem
	 * @returns {string} Data formatada
	 */
	const formatHeaderDate = (timestamp) => {
		if (!timestamp) return "";

		try {
			let date;
			const timestampNum = Number(timestamp);

			if (Number.isNaN(timestampNum) || timestampNum <= 0) {
				return "";
			}

			// Se o timestamp está em segundos (< 10 bilhões), converte para milissegundos
			if (timestampNum < 10000000000) {
				date = fromUnixTime(timestampNum);
			} else {
				// Timestamp já está em milissegundos
				date = new Date(timestampNum);
			}

			// Verifica se a data é válida
			if (!isValid(date)) {
				return "";
			}

			// Usa as funções do date-fns para verificar hoje e ontem
			if (isToday(date)) {
				// Para hoje: mostra apenas a hora
				return format(date, "HH:mm");
			}

			if (isYesterday(date)) {
				// Para ontem: mostra "Ontem"
				return "Ontem";
			}

			// Calcula a diferença em dias usando date-fns
			const daysDiff = differenceInDays(startOfDay(new Date()), startOfDay(date));

			// Últimos 7 dias (mas não ontem nem hoje): mostra o dia da semana
			if (daysDiff >= 2 && daysDiff <= 7) {
				return format(date, "EEEE", { locale: ptBR });
			}

			// Mais de 7 dias: mostra a data completa
			return format(date, "dd/MM/yyyy", { locale: ptBR });
		} catch (error) {
			console.error("Erro ao formatar data do header:", error, "timestamp:", timestamp);
			return "";
		}
	};

	/**
	 * @param {ChatInfo} chat - Informações do chat
	 */
	const handleChatSelect = (chat) => {
		setSelectedChat(chat);
	};

	/**
	 * @param {React.KeyboardEvent} event - Evento de teclado
	 * @param {ChatInfo} chat - Informações do chat
	 */
	const handleKeyboardSelect = (event, chat) => {
		if (event.key === "Enter" || event.key === " ") {
			setSelectedChat(chat);
		}
	};

	/**
	 * Função para alternar a visibilidade da sidebar de conversas
	 * @param {React.MouseEvent} e - Evento de clique
	 */
	const toggleSidebar = (e) => {
		e.stopPropagation();
		setIsSidebarMinimized(!isSidebarMinimized);
	};

	/**
	 * @param {string} messageId - ID da mensagem
	 * @returns {boolean} Verdadeiro se a mensagem for nova
	 */
	const isNewMessage = (messageId) => {
		return newMessageIds.includes(messageId);
	};

	/**
	 * Handler para envio de mensagem
	 * @param {React.FormEvent} e - Evento de formulário
	 */
	const handleSendMessage = async (e) => {
		e.preventDefault();

		if (!messageInput.trim() || !selectedChat || !instanceId || isSending) {
			return;
		}

		setIsSending(true);

		try {
			const result = await ipcRenderer.invoke("send-message", {
				instanceId,
				chatId: selectedChat.id,
				message: messageInput,
			});

			if (!result.success) {
				console.error("Erro ao enviar mensagem:", result.error);
			}

			setMessageInput("");

			setTimeout(() => {
				if (messageInputRef.current) {
					messageInputRef.current.focus();
				}
			}, 50);
		} catch (error) {
			console.error("Erro ao enviar mensagem:", error);
		} finally {
			setIsSending(false);

			// Adiciona um foco adicional após o término do envio para maior garantia
			setTimeout(() => {
				if (messageInputRef.current) {
					messageInputRef.current.focus();
				}
			}, 100);
		}
	};

	/**
	 * Handler para pressionar Enter no campo de mensagem
	 * @param {React.KeyboardEvent} e - Evento de teclado
	 */
	const handleKeyPress = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage(e);
		}
	};

	/**
	 * @param {Message} msg - Mensagem a verificar
	 * @returns {boolean} Verdadeiro se for mensagem da IA
	 */
	const isAIMessage = (msg) => {
		return !!msg.isAIMessage;
	};

	/**
	 * @param {Message} msg - Mensagem a verificar
	 * @returns {boolean} Verdadeiro se for mensagem de follow-up
	 */
	const isFollowUpMessage = (msg) => {
		return !!msg.isFollowUp;
	};

	/**
	 * @param {Message} msg - Objeto da mensagem
	 * @returns {JSX.Element|null} Elemento React ou null se o tipo não for suportado
	 */
	const renderMessageContent = (msg) => {
		const msgContent = msg.message;

		if (!msgContent) return null;

		if (msgContent.conversation) {
			return (
				<div className="text-sm whitespace-pre-wrap break-words">
					{msgContent.conversation}
				</div>
			);
		}

		if (msgContent.extendedTextMessage?.text) {
			return (
				<div className="text-sm whitespace-pre-wrap break-words">
					{msgContent.extendedTextMessage.text}
				</div>
			);
		}

		if (msgContent.imageMessage) {
			return (
				<MediaMessage type="image" caption={msgContent.imageMessage.caption} />
			);
		}

		if (msgContent.videoMessage) {
			return (
				<MediaMessage type="video" caption={msgContent.videoMessage.caption} />
			);
		}

		if (msgContent.audioMessage) {
			return (
				<AudioMessagePlayer
					message={msg}
					instanceId={instanceId}
					selectedChat={selectedChat}
					isFromMe={msg.key?.fromMe}
					isAI={isAIMessage(msg)}
				/>
			);
		}

		if (msgContent.documentMessage) {
			return (
				<MediaMessage
					type="document"
					caption={msgContent.documentMessage.fileName}
				/>
			);
		}

		if (msgContent.stickerMessage) {
			return <MediaMessage type="sticker" />;
		}

		if (msgContent.contactMessage || msgContent.contactsArrayMessage) {
			return <MediaMessage type="contact" caption="Contato compartilhado" />;
		}

		if (msgContent.locationMessage) {
			return (
				<MediaMessage type="location" caption="Localização compartilhada" />
			);
		}

		if (msgContent.buttonsMessage || msgContent.templateMessage) {
			return <MediaMessage type="interactive" caption="Mensagem interativa" />;
		}

		if (msgContent.pollCreationMessage || msgContent.pollUpdateMessage) {
			return <MediaMessage type="poll" caption="Enquete" />;
		}

		return null;
	};

	/** @type {[number, React.Dispatch<React.SetStateAction<number>>]} */
	const [followUpCheckTimeLeft, setFollowUpCheckTimeLeft] = useState(0);

	useEffect(() => {
		if (!selectedChat || !instanceId) return;

		const loadFollowUpCheckInfo = () => {
			ipcRenderer
				.invoke("get-follow-up-check-info", { instanceId, chatId: selectedChat.id })
				.then((info) => {

					if (info.hasScheduledCheck) {
						const now = Date.now();
						const timeLeft = info.checkTime - now;
						
						
						// ✅ CORREÇÃO: Calcula tempo restante imediatamente
						if (timeLeft > 0) {
							setFollowUpCheckTimeLeft(timeLeft);
						} else {
							setFollowUpCheckTimeLeft(0);
						}
						
						setFollowUpChecks(prev => {

							if (prev[selectedChat.id] === info.checkTime) {
								return prev;
							}

							const newState = {
								...prev,
								[selectedChat.id]: info.checkTime
							};
							return newState;
						});
					} else {
						
						setFollowUpChecks(prev => {
							if (!prev[selectedChat.id]) {
								return prev;
							}

							const newChecks = { ...prev };
							delete newChecks[selectedChat.id];
							return newChecks;
						});

						setFollowUpCheckTimeLeft(0);
					}
				})
				.catch((error) => {
					console.error("Erro ao obter informações de verificação de follow-up:", error);
				});
		};

		loadFollowUpCheckInfo();

		const checkInterval = setInterval(loadFollowUpCheckInfo, 30000);

		return () => clearInterval(checkInterval);
	}, [selectedChat?.id, instanceId]);

	const currentFollowUpCheck = useMemo(() => {
		return selectedChat ? followUpChecks[selectedChat.id] : null;
	}, [followUpChecks, selectedChat?.id]);

	useEffect(() => {

		if (!selectedChat || !currentFollowUpCheck || followUpCheckTimeLeft <= 0) {
			return;
		}

		let tickCount = 0;

		const timer = setInterval(() => {
			tickCount++;
			
			setFollowUpCheckTimeLeft(prev => {
				const newTimeLeft = Math.max(0, prev - 1000);
				

				if (newTimeLeft <= 0) {
					setFollowUpChecks(current => {
						const newChecks = { ...current };
						delete newChecks[selectedChat.id];
						return newChecks;
					});
					clearInterval(timer);
				}

				return newTimeLeft;
			});
		}, 1000);

		return () => {
			clearInterval(timer);
		};
	}, [selectedChat?.id, currentFollowUpCheck, followUpCheckTimeLeft]);

	/**
	 * Cancela um follow-up agendado
	 * @param {string} chatId - ID do chat
	 * @param {number} followUpId - ID do follow-up (scheduledTime)
	 */
	const handleCancelFollowUp = useCallback(async (chatId, followUpId) => {
		try {
			const result = await ipcRenderer.invoke("cancel-follow-up", {
				instanceId,
				chatId,
				followUpId
			});

			if (result.success) {
				toast.success("Follow-up cancelado com sucesso");

				ipcRenderer
					.invoke("get-active-follow-ups", instanceId)
					.then((followUps) => {
						setActiveFollowUps(followUps || {});
					})
					.catch((error) => {
						console.error("Erro ao atualizar follow-ups:", error);
					});
			} else {
				toast.error(result.message || "Erro ao cancelar follow-up");
			}
		} catch (error) {
			toast.error("Erro ao cancelar follow-up");
			console.error("Erro ao cancelar follow-up:", error);
		}
	}, [instanceId]);

	/**
	 * Envia um follow-up imediatamente
	 * @param {string} chatId - ID do chat
	 * @param {number} followUpId - ID do follow-up (scheduledTime)
	 */
	const handleSendFollowUpNow = useCallback(async (chatId, followUpId) => {
		try {
			const result = await ipcRenderer.invoke("send-follow-up-now", {
				instanceId,
				chatId,
				followUpId
			});

			if (result.success) {
				toast.success("Follow-up enviado com sucesso");

				ipcRenderer
					.invoke("get-active-follow-ups", instanceId)
					.then((followUps) => {
						setActiveFollowUps(followUps || {});
					})
					.catch((error) => {
						console.error("Erro ao atualizar follow-ups:", error);
					});
			} else {
				toast.error(result.message || "Erro ao enviar follow-up");
			}
		} catch (error) {
			toast.error("Erro ao enviar follow-up");
			console.error("Erro ao enviar follow-up:", error);
		}
	}, [instanceId]);

	/**
	 * Verifica imediatamente a elegibilidade para follow-up (MANUAL)
	 * (Esta função é responsável por toasts de verificações manuais do usuário)
	 * @param {string} chatId - ID do chat
	 */
	const handleCheckFollowUpNow = useCallback(async (chatId) => {
		if (isCheckingFollowUp) return;

		setIsCheckingFollowUp(true);

		try {
			const result = await ipcRenderer.invoke("check-follow-up-now", {
				instanceId,
				chatId
			});

			if (result.success) {
				if (result.cancelledAutomaticCheck) {
					setFollowUpChecks(prev => {
						const newChecks = { ...prev };
						delete newChecks[chatId];
						return newChecks;
					});
					setFollowUpCheckTimeLeft(0);
				}

				// Só exibe toasts se foi uma verificação MANUAL (pelo usuário)
				// Verificações automáticas são tratadas pelo listener followUpCheckResultListener
				if (result.isManualCheck !== false) {
					const baseToastId = `manual-check-${chatId}`;

					if (result.hasFollowUp) {
						if (!toast.isActive(`${baseToastId}-success`)) {
							toast.success(result.message, {
								position: "top-right",
								autoClose: 5000,
								toastId: `${baseToastId}-success`
							});
						}
					} else {
						if (!toast.isActive(`${baseToastId}-info`)) {
							toast.info(result.message, {
								position: "top-right",
								autoClose: 5000,
								toastId: `${baseToastId}-info`
							});
						}
					}

					if (result.reason && !toast.isActive(`${baseToastId}-reason`)) {
						toast.info(result.reason, {
							position: "top-right",
							autoClose: 8000,
							delay: 1000,
							toastId: `${baseToastId}-reason`
						});
					}
				}

				// ✅ SEMPRE atualiza a lista de follow-ups após verificação manual
				ipcRenderer
					.invoke("get-active-follow-ups", instanceId)
					.then((followUps) => {
						setActiveFollowUps(followUps || {});
					})
					.catch((error) => {
						console.error("Erro ao atualizar follow-ups:", error);
					});
			} else {
				const errorToastId = `${chatId}-error`;
				if (!toast.isActive(errorToastId)) {
					toast.error(result.message, {
						position: "top-right",
						autoClose: 5000,
						toastId: errorToastId
					});
				}
			}
		} catch (error) {
			const errorToastId = `${chatId}-error`;
			if (!toast.isActive(errorToastId)) {
				toast.error("Erro ao verificar follow-up", {
					position: "top-right",
					autoClose: 5000,
					toastId: errorToastId
				});
			}
			console.error("Erro ao verificar follow-up:", error);
		} finally {
			setIsCheckingFollowUp(false);
		}
	}, [instanceId, isCheckingFollowUp]);

	/**
	 * @param {string} chatId - ID do chat a ser limpo
	 */
	const handleClearChat = useCallback(async (chatId) => {
		if (!chatId || isClearingChat) return;

		const confirmed = window.confirm("Tem certeza que deseja limpar toda a conversa deste chat? Esta ação não pode ser desfeita.");
		if (!confirmed) return;

		setIsClearingChat(true);

		try {
			const result = await ipcRenderer.invoke("clear-chat-conversation", {
				instanceId,
				chatId
			});

			if (result.success) {
				toast.success("Conversa limpa com sucesso");
				setMessages([]);
				ipcRenderer
					.invoke("get-chats", instanceId)
					.then((chatsInfo) => {
						const filteredChats = chatsInfo.filter((chat) => {
							const hasValidMessage =
								chat.lastMessage && chat.lastMessage.trim() !== "";
							const hasValidTimestamp = chat.timestamp && chat.timestamp > 0;
							return hasValidMessage && hasValidTimestamp;
						});
						setChats(filteredChats);
					})
					.catch((error) => {
						console.error("Erro ao recarregar chats:", error);
					});
			} else {
				toast.error(result.message || "Erro ao limpar conversa");
			}
		} catch (error) {
			console.error("Erro ao limpar conversa:", error);
			toast.error("Erro ao limpar conversa");
		} finally {
			setIsClearingChat(false);
		}
	}, [instanceId, isClearingChat]);

	const ChatHeader = ({
		selectedChat,
		allInterventions,
		followUpCheckTimeLeft,
		followUpChecks,
		isCheckingFollowUp,
		handleCheckFollowUpNow,
		handleSendFollowUpNow,
		handleCancelFollowUp,
		activeFollowUps,
		handleClearChat,
		isClearingChat,
		followUpConditions,
		headerButtonState,
		handleAIModeStatusChange,
		instanceId,
		setAllInterventions,
		forceRefreshAIStates
	}) => {
		if (!selectedChat) return null;

		// Dados reais da intervenção para o timer
		const intervention = allInterventions[selectedChat.id] || {};
		const isHumanInterventionActive = !!intervention || headerButtonState?.isHumanMode;
		const isManual = intervention.isManualIntervention || false;
		const [remainingTime, setRemainingTime] = useState(
			intervention.remainingTime || 0,
		);

		// Calcula os follow-ups e o próximo follow-up dentro do componente
		const followUps = useMemo(() => {
			return activeFollowUps[selectedChat.id] || [];
		}, [activeFollowUps, selectedChat.id]);

		const nextFollowUp = useMemo(() => {
			return followUps.length > 0 ? followUps.sort((a, b) => a.scheduledTime - b.scheduledTime)[0] : null;
		}, [followUps]);

		const hasActiveTimer = !!followUpChecks[selectedChat.id];

		// Timer para intervenções temporárias
		useEffect(() => {
			setRemainingTime(intervention.remainingTime || 0);

			// Só inicia timer se:
			// 1. Há intervenção humana ativa
			// 2. NÃO é intervenção manual (só temporárias têm timer)
			// 3. Há tempo restante
			if (
				!isHumanInterventionActive ||
				isManual ||
				!intervention.remainingTime
			) {
				return;
			}

			const timer = setInterval(() => {
				setRemainingTime((prev) => {
					const newTime = Math.max(0, prev - 1000);
					return newTime;
				});
			}, 1000);

			return () => clearInterval(timer);
		}, [isHumanInterventionActive, isManual, intervention.remainingTime]);

		const formatRemainingTime = useCallback((milliseconds) => {
			if (!milliseconds || milliseconds <= 0) return null;

			const MILLISECONDS_PER_SECOND = 1000;
			const MILLISECONDS_PER_MINUTE = 60000;
			const MILLISECONDS_PER_HOUR = 3600000;

			const totalSeconds = Math.floor(milliseconds / MILLISECONDS_PER_SECOND);
			
			if (totalSeconds < 60) {
				return `${totalSeconds}s`;
			}

			const totalMinutes = Math.floor(milliseconds / MILLISECONDS_PER_MINUTE);
			
			if (totalMinutes < 60) {
				return `${totalMinutes}m`;
			}

			const hours = Math.floor(milliseconds / MILLISECONDS_PER_HOUR);
			const remainingMinutes = Math.floor((milliseconds % MILLISECONDS_PER_HOUR) / MILLISECONDS_PER_MINUTE);
			
			return `${hours}h ${remainingMinutes.toString().padStart(2, "0")}m`;
		}, []);

		const handleCheckFollowUpClick = useCallback((e) => {
			e.preventDefault();
			e.stopPropagation();
			handleCheckFollowUpNow(selectedChat.id);
		}, [selectedChat.id, handleCheckFollowUpNow]);

		const handleSendFollowUpClick = useCallback(() => {
			if (nextFollowUp?.scheduledTime) {
				handleSendFollowUpNow(selectedChat.id, nextFollowUp.scheduledTime);
			}
		}, [selectedChat.id, nextFollowUp?.scheduledTime, handleSendFollowUpNow]);

		const handleCancelFollowUpClick = useCallback(() => {
			if (nextFollowUp?.scheduledTime) {
				handleCancelFollowUp(selectedChat.id, nextFollowUp.scheduledTime);
			}
		}, [selectedChat.id, nextFollowUp?.scheduledTime, handleCancelFollowUp]);

		return (
			<div className="flex flex-col border-b bg-slateContent/80">
				<div className="flex justify-between items-center p-4">
					<div className="flex items-center">
						<span className="text-lg font-medium text-textPrimary">
							{selectedChat.name || "Chat"}
						</span>
					</div>

					<div className="flex items-center">
						{isHumanInterventionActive && !isManual && remainingTime > 0 && (
							<div className="mr-3 flex items-center px-3 py-1 rounded-md bg-statusBrandDark/20 border border-statusBrand/30">
								<Clock size={14} className="text-statusBrandLight mr-2" />
								<div className="flex flex-col">
									<span className="text-xs text-textMuted">Reativação em</span>
									<span className="text-sm font-semibold text-statusBrandLight">
										{formatRemainingTime(remainingTime)}
									</span>
								</div>
							</div>
						)}

						<div
							className={`flex items-center px-3 py-1 rounded-md ${headerButtonState?.effectiveHumanMode ? "bg-statusBrandDark/20 border border-statusBrand/30" : "bg-primaryDark/20 border border-primaryColor/30"} mr-3`}
						>
							<span
								className={`text-sm ${headerButtonState?.effectiveHumanMode ? "text-statusBrandLight" : "text-textPrimary"}`}
							>
								{headerButtonState?.reason || "Carregando..."}
							</span>
						</div>

						<button
							type="button"
							onClick={async () => {
								// Aciona diretamente o sistema IA e garante sincronização total
								try {
									const currentlyActive = !headerButtonState?.effectiveHumanMode;
									const newActiveState = !currentlyActive;
									
									const result = await ipcRenderer.invoke("set-ai-mode", {
										instanceId,
										chatId: selectedChat.id,
										active: newActiveState
									});

									if (result.success) {
										// 1. Atualiza estado do sistema IA (para header)
										handleAIModeStatusChange(selectedChat.id, result.status);
										
										// 2. Se ativou IA e havia intervenção, recarrega lista de intervenções
										if (newActiveState && headerButtonState?.isHumanMode) {
											try {
												const interventions = await ipcRenderer.invoke("get-all-human-interventions");
												setAllInterventions(interventions || {});
											} catch (error) {
												console.error("Erro ao atualizar lista de intervenções:", error);
											}
										}
										
										// 3. Força atualização dos estados para garantir sincronização total
										// Usa a função dedicada para isso
										await forceRefreshAIStates(selectedChat.id);
										
										// 4. Pequeno delay para garantir que toda a UI atualize (sidebar)
										setTimeout(async () => {
											await forceRefreshAIStates(selectedChat.id);
										}, 100);
										
									} else {
										toast.info(result.message, {
											position: "top-right",
											autoClose: 6000
										});
									}
								} catch (error) {
									console.error("Erro ao alterar modo IA:", error);
									toast.error("Erro ao alterar modo IA");
								}
							}}
							title={headerButtonState?.reason}
							className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors cursor-pointer ${headerButtonState?.effectiveHumanMode ? "bg-statusBrandDark" : "bg-primaryDark"
								} hover:opacity-90 active:scale-95`}
						>
							<span
								className={`absolute left-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full transition-all text-white ${!headerButtonState?.effectiveHumanMode ? "opacity-100" : "opacity-40"
									}`}
							>
								<BotIcon size={12} />
							</span>

							<span
								className={`absolute top-0.5 flex h-5 w-5 transform items-center justify-center rounded-full bg-white transition-transform ${headerButtonState?.effectiveHumanMode ? "translate-x-0.5" : "translate-x-6"
									}`}
							/>

							<span
								className={`absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full transition-all text-white ${headerButtonState?.effectiveHumanMode ? "opacity-100" : "opacity-40"
									}`}
							>
								<UserIcon size={12} />
							</span>
						</button>

						<button
							type="button"
							onClick={() => handleClearChat(selectedChat.id)}
							disabled={isClearingChat}
							className={`ml-3 flex items-center justify-center h-8 w-8 rounded-full transition-colors ${isClearingChat
								? "bg-statusErrorLight cursor-not-allowed"
								: "bg-statusErrorDark hover:bg-statusError"
								}`}
							title="Limpar conversa"
						>
							{isClearingChat ? (
								<div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
							) : (
								<Trash2 size={16} className="text-white" />
							)}
						</button>
					</div>
				</div>

				{/* Container completamente estável para seções de follow-up */}
				<div className="follow-up-sections">
					{/* Seção azul - somente exibe se não há follow-up agendado */}
					{followUpConditions.shouldShowBlueSection && (
						<div className="px-4 py-2 bg-dashboardAccent/20 border-t border-b border-dashboardAccent/30 flex items-center justify-between">
							<div className="flex items-center">
								<Bell size={16} className="text-dashboardAccent mr-2" />
								<div>
									<span className="text-xs text-dashboardAccent font-medium">Follow-up</span>
									<p className="text-sm text-textSecondary">
										{followUpChecks[selectedChat.id]
											? "Verificação automática agendada"
											: "Gerencie follow-ups para este chat"}
									</p>
								</div>
							</div>
							<div className="flex flex-col gap-2 items-end">
								{followUpChecks[selectedChat.id] && followUpCheckTimeLeft > 0 && (
									<div className="flex items-center bg-dashboardAccent/40 px-3 py-1 rounded-md border border-dashboardAccent/30">
										<Clock size={14} className="text-dashboardAccent mr-2" />
										<span className="text-sm text-dashboardAccent">
											{formatRemainingTime(followUpCheckTimeLeft)}
										</span>
									</div>
								)}

								<div className="flex gap-2">
									<button
										type="button"
										onClick={handleCheckFollowUpClick}
										disabled={isCheckingFollowUp}
										className={`flex items-center justify-center text-white text-xs rounded px-3 py-1 transition-colors ${isCheckingFollowUp
											? 'bg-dashboardAccent cursor-not-allowed'
											: 'bg-primaryDark hover:bg-primaryColor'
											}`}
									>
										{isCheckingFollowUp ? (
											<>
												<div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
												Verificando...
											</>
										) : (
											<>
												<Clock size={12} className="mr-1" />
												{hasActiveTimer ? "Verificar agora" : "Verificar"}
											</>
										)}
									</button>
								</div>
							</div>
						</div>
					)}

					{/* Seção amarela - follow-up agendado */}
					{followUpConditions.shouldShowYellowSection && (
						<div className="px-4 py-2 bg-statusYellowDarkest/20 border-t border-b border-statusYellowDark/30 flex items-center justify-between">
							<div className="flex items-center">
								<Bell size={16} className="text-statusYellowLight mr-2" />
								<div>
									<span className="text-xs text-statusYellowLight font-medium">Follow-up agendado</span>
									<p className="text-sm text-white">"{nextFollowUp?.message}"</p>
								</div>
							</div>
							<div className="flex flex-col gap-2">
								<FollowUpSendTimer
									followUps={followUps}
									formatTime={formatRemainingTime}
								/>
								<div className="flex gap-2">
									<button
										type="button"
										onClick={handleSendFollowUpClick}
										className="flex items-center justify-center bg-statusSuccessDark hover:bg-statusSuccess text-white text-xs rounded px-3 py-1 transition-colors"
									>
										<SendIcon size={12} className="mr-1" />
										Enviar agora
									</button>
									<button
										type="button"
										onClick={handleCancelFollowUpClick}
										className="flex items-center justify-center bg-statusErrorDark hover:bg-statusError text-white text-xs rounded px-3 py-1 transition-colors"
									>
										<XIcon size={12} className="mr-1" />
										Cancelar
									</button>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Painel Tempo e Mensagens (habilitado pelo botão) */}
				{showStats && (
					<div className="px-4 py-3 border-t bg-slate-800/60">
						{(() => {
							const stats = computeChatStats(messages);
							return (
								<div className="flex flex-col gap-2">
									<div className="flex items-center gap-4">
										<span className="text-sm text-textMuted">Total de mensagens:</span>
										<span className="font-semibold">{stats.messageCount}</span>
									</div>
									<div className="flex items-center gap-4">
										<span className="text-sm text-textMuted">Pares usuário→bot:</span>
										<span className="font-semibold">{stats.responsePairs}</span>
									</div>
									<div className="flex items-center gap-4">
										<span className="text-sm text-textMuted">Tempo médio de resposta:</span>
										<span className="font-semibold">{stats.avgResponseMs ? `${Math.round(stats.avgResponseMs/1000)}s` : '—'}</span>
									</div>
								</div>
							);
						})()}
					</div>
				)}

					{/* Configurações de alerta dentro do painel Tempo e Mensagens */}
					{showStats && (
						<div className="px-4 py-3 border-t bg-slate-900/40">
							<div className="flex flex-col gap-2 max-w-md">
								<label className="text-sm text-textMuted">Número para receber alertas</label>
								<div className="flex gap-2">
									<input
										type="text"
										value={typeof alertNumber !== 'undefined' ? alertNumber : (settings?.ALERT_NUMBER || '')}
										onChange={(e) => setAlertNumber(e.target.value.replace(/\D/g, ''))}
										placeholder="5511999999999"
										className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-sm"
									/>
									<button
										type="button"
										onClick={async () => {
											setIsSavingAlertSettings(true);
											try {
												await ipcRenderer.invoke('save-setting', { instanceId, key: 'ALERT_NUMBER', value: (alertNumber || '').replace(/\D/g, '') });
												await ipcRenderer.invoke('save-setting', { instanceId, key: 'ALERT_ENABLED', value: alertEnabledLocal });
												toast.success('Configurações de alerta salvas');
											} catch (err) {
												console.error('Erro ao salvar alert settings', err);
												toast.error('Erro ao salvar configurações');
											} finally {
												setIsSavingAlertSettings(false);
											}
										}}
										className="px-3 py-2 rounded bg-primaryDark text-white"
									>
										Salvar
									</button>
								</div>
								<div className="flex items-center gap-2 mt-2">
									<input type="checkbox" checked={alertEnabledLocal} onChange={(e)=>setAlertEnabledLocal(e.target.checked)} />
									<span className="text-sm text-textMuted">Alerta habilitado</span>
								</div>
								<div className="flex gap-2 mt-3">
									<button
										type="button"
										onClick={async () => {
											setIsSendingTestAlert(true);
											try {
												const res = await ipcRenderer.invoke('send-test-alert', { instanceId, chatId: selectedChat?.id, reason: 'TEST_ALERT_FROM_UI', preview: 'Teste de alerta manual' });
												if (res.success) toast.success('Alerta de teste enviado'); else toast.error('Falha ao enviar alerta de teste');
											} catch (err) {
												console.error('Erro ao enviar alerta de teste', err);
												toast.error('Erro ao enviar alerta de teste');
											} finally { setIsSendingTestAlert(false); }
										}}
										className="px-3 py-2 rounded bg-primaryDark text-white"
									>
										{isSendingTestAlert ? 'Enviando...' : 'Enviar alerta de teste'}
									</button>
								</div>
							</div>
						</div>
					)}
			</div>
		);
	};

	// Adicionar após a função renderMessageContent (linha ~1149):

	/**
	 * Formata separador de data seguindo a lógica do WhatsApp
	 * @param {number|string} timestamp - Timestamp da mensagem
	 * @returns {string} Data formatada para separador
	 */
	const formatDateSeparator = (timestamp) => {
		if (!timestamp) return "";

		try {
			let date;
			const timestampNum = Number(timestamp);

			if (Number.isNaN(timestampNum) || timestampNum <= 0) {
				return "";
			}

			// Se o timestamp está em segundos (< 10 bilhões), converte para milissegundos
			if (timestampNum < 10000000000) {
				date = fromUnixTime(timestampNum);
			} else {
				// Timestamp já está em milissegundos
				date = new Date(timestampNum);
			}

			// Verifica se a data é válida
			if (!isValid(date)) {
				return "";
			}

			// Usa as funções do date-fns para verificar hoje e ontem
			if (isToday(date)) {
				return "Hoje";
			}

			if (isYesterday(date)) {
				return "Ontem";
			}

			// Calcula a diferença em dias usando date-fns
			const daysDiff = differenceInDays(startOfDay(new Date()), startOfDay(date));

			// Últimos 7 dias (mas não ontem nem hoje): mostra o dia da semana
			if (daysDiff >= 2 && daysDiff <= 7) {
				return format(date, "EEEE", { locale: ptBR });
			}

			// Mais de 7 dias: mostra a data completa
			return format(date, "dd/MM/yyyy", { locale: ptBR });
		} catch (error) {
			console.error("Erro ao formatar separador de data:", error, "timestamp:", timestamp);
			return "";
		}
	};

	/**
	 * Verifica se duas datas são do mesmo dia
	 * @param {number|string} timestamp1 - Primeiro timestamp
	 * @param {number|string} timestamp2 - Segundo timestamp
	 * @returns {boolean} True se são do mesmo dia
	 */
	const isSameDay = (timestamp1, timestamp2) => {
		if (!timestamp1 || !timestamp2) return false;

		try {
			const parseTimestamp = (ts) => {
				const timestampNum = Number(ts);
				if (Number.isNaN(timestampNum) || timestampNum <= 0) {
					return null;
				}

				// Se o timestamp está em segundos (< 10 bilhões), converte para milissegundos
				if (timestampNum < 10000000000) {
					return fromUnixTime(timestampNum);
				} else {
					// Timestamp já está em milissegundos
					return new Date(timestampNum);
				}
			};

			const date1 = parseTimestamp(timestamp1);
			const date2 = parseTimestamp(timestamp2);

			if (!date1 || !date2 || !isValid(date1) || !isValid(date2)) {
				return false;
			}

			return format(date1, "yyyy-MM-dd") === format(date2, "yyyy-MM-dd");
		} catch (error) {
			console.warn("Erro ao comparar datas:", error);
			return false;
		}
	};

	return (
		<div className="fixed inset-0 bottom-24 flex flex-col bg-slateBase text-white">
			<ConnectionAlert 
				connected={connectionStatus.connected} 
				isMediaModalOpen={isMediaModalOpen}
				setIsMediaModalOpen={setIsMediaModalOpen}
				selectedChat={selectedChat}
			/>

			<div className="flex flex-1 overflow-hidden pb-2">
				<div className={`${isSidebarMinimized ? 'w-12' : 'w-1/3'} flex flex-col border-r border-emeraldMedium/20 bg-slateCard/80 transition-all duration-300 ease-in-out relative`}>
					<div className="p-4 border-b border-emeraldMedium/20 bg-slateContent/80 flex-shrink-0 flex items-center justify-between">
						{!isSidebarMinimized && (
							<h1 className="text-lg font-bold text-textPrimary">Conversas</h1>
						)}
						<button
							onClick={toggleSidebar}
							className="p-2 rounded-lg bg-primaryColor/10 hover:bg-primaryColor/20 text-primaryLight transition-colors duration-200 flex-shrink-0"
							title={isSidebarMinimized ? "Expandir conversas" : "Minimizar conversas"}
						>
							{isSidebarMinimized ? (
								<ChevronRight size={16} />
							) : (
								<ChevronLeft size={16} />
							)}
						</button>
					</div>

					<div className="overflow-y-auto flex-1 chat-scrollbar">
						{chats.length === 0 ? (
							<div className={`${isSidebarMinimized ? 'p-2' : 'p-6'} text-center text-textMuted`}>
								{isSidebarMinimized ? '😔' : 'Nenhuma conversa encontrada'}
							</div>
						) : (
							<ul className="list-none">
								{chats.map((chat) => {
									const chatName = chat.name || (chat.isGroup ? `${chat.id.split("@")[0]}` : chat.id.split("@")[0]);
									const firstLetter = chatName.charAt(0).toUpperCase();

									return (
										<button
											type="button"
											key={chat.id}
											className={`w-full text-left ${isSidebarMinimized ? 'p-2' : 'p-4'} border-b border-emeraldMedium/5 hover:bg-emeraldDark/10 transition-all duration-200 ${selectedChat?.id === chat.id
												? "bg-emeraldDark/20"
												: "bg-transparent"
												}`}
											onClick={() => handleChatSelect(chat)}
											onKeyDown={(e) => handleKeyboardSelect(e, chat)}
											title={isSidebarMinimized ? chatName : undefined}
										>
											{isSidebarMinimized ? (
												// Versão minimizada - só ícone/letra
												<div className="flex flex-col items-center">
													<div className="w-8 h-8 rounded-full bg-primaryColor/20 flex items-center justify-center mb-1">
														{chat.isGroup ? (
															<Users size={14} className="text-primaryLight" />
														) : (
															<span className="text-xs font-bold text-textPrimary">
																{firstLetter}
															</span>
														)}
													</div>
													{activeFollowUps[chat.id] && (
														<Bell size={10} className="text-statusYellow" />
													)}
												</div>
											) : (
												// Versão expandida - layout completo
												<>
													<div className="flex justify-between">
														<div className="flex items-center">
															{chat.isGroup && (
																<span className="text-xs bg-primaryColor/20 text-primaryLight px-2 py-0.5 rounded mr-2">
																	Grupo
																</span>
															)}
															<h3 className="font-semibold text-textPrimary">
																{chatName}
															</h3>

															{activeFollowUps[chat.id] && (
																<span className="ml-2 text-statusYellowLight" title="Follow-up agendado">
																	<Bell size={16} />
																</span>
															)}

															<AIModeToggle
																chatId={chat.id}
																instanceId={instanceId}
																aiModeStatus={aiModeStatuses[chat.id]}
																onStatusChange={handleAIModeStatusChange}
															/>
														</div>
														<span className="text-xs text-textMuted">
															{formatHeaderDate(chat.timestamp)}
														</span>
													</div>
													<p className="text-sm text-textSecondary truncate mt-1">
														{chat.lastMessageFromMe ? (
															<span className="font-medium text-cyanTertiary">Você: </span>
														) : chat.isGroup && chat.lastMessageSender ? (
															`${chat.lastMessageSender}: `
														) : ""}
														{chat.lastMessage}
													</p>
												</>
											)}
										</button>
									);
								})}
							</ul>
						)}
					</div>
				</div>

				<div className={`${isSidebarMinimized ? 'flex-1' : 'w-2/3'} flex flex-col bg-slateCard/50 transition-all duration-300 ease-in-out`}>
					{selectedChat ? (
						<>
							<ChatHeader
								selectedChat={selectedChat}
								allInterventions={allInterventions}
								followUpCheckTimeLeft={followUpCheckTimeLeft}
								followUpChecks={followUpChecks}
								isCheckingFollowUp={isCheckingFollowUp}
								handleCheckFollowUpNow={handleCheckFollowUpNow}
								handleSendFollowUpNow={handleSendFollowUpNow}
								handleCancelFollowUp={handleCancelFollowUp}
								activeFollowUps={activeFollowUps}
								handleClearChat={handleClearChat}
								isClearingChat={isClearingChat}
								followUpConditions={followUpConditions}
								headerButtonState={headerButtonState}
								handleAIModeStatusChange={handleAIModeStatusChange}
								instanceId={instanceId}
								setAllInterventions={setAllInterventions}
								forceRefreshAIStates={forceRefreshAIStates}
							/>

							<div className="flex-1 p-4 overflow-y-auto chat-scrollbar">
								{messages.length === 0 ? (
									<div className="text-center text-textMuted my-8">
										Nenhuma mensagem encontrada neste chat
									</div>
								) : (
									<div className="space-y-4">
										{(() => {
											const messagesWithSeparators = [];

											messages.forEach((msg, index) => {
												const isFromMe = msg.key?.fromMe;
												const isAI = isAIMessage(msg);
												const messageId = msg.key?.id || `msg-${index}`;
												const messageContent = renderMessageContent(msg);

												// Testa múltiplas fontes de timestamp
												const getValidTimestamp = () => {
													const timestampSources = [
														msg.messageTimestamp,
														msg.key?.timestamp,
														msg.timestamp,
														msg.t,
														Date.now() / 1000
													];

													for (const ts of timestampSources) {
														if (ts && Number(ts) > 0) {
															return ts;
														}
													}

													return Date.now() / 1000;
												};

												const timestamp = getValidTimestamp();

												if (messageContent === null) return;

												// Verifica se precisa adicionar separador de data
												let shouldShowSeparator = false;

												if (index === 0) {
													// Primeira mensagem: sempre mostra separador
													shouldShowSeparator = true;
												} else {
													// Para outras mensagens, verifica se é um dia diferente da anterior
													const prevMsg = messages[index - 1];

													// Usa a mesma função para obter timestamp da mensagem anterior
													const getPrevValidTimestamp = () => {
														const timestampSources = [
															prevMsg.messageTimestamp,
															prevMsg.key?.timestamp,
															prevMsg.timestamp,
															prevMsg.t,
															Date.now() / 1000
														];

														for (const ts of timestampSources) {
															if (ts && Number(ts) > 0) {
																return ts;
															}
														}

														return Date.now() / 1000;
													};

													const prevTimestamp = getPrevValidTimestamp();

													if (!isSameDay(timestamp, prevTimestamp)) {
														shouldShowSeparator = true;
													}
												}

												if (shouldShowSeparator) {
													const dateSeparator = formatDateSeparator(timestamp);
													if (dateSeparator) {
														messagesWithSeparators.push(
															<div key={`date-separator-${index}`} className="flex justify-center my-6">
																<div className="bg-emeraldDark/20 text-textPrimary text-xs px-3 py-1 rounded-full">
																	{dateSeparator}
																</div>
															</div>
														);
													}
												}

												const isHighlighted = isNewMessage(messageId);

												// Adiciona a mensagem
												messagesWithSeparators.push(
													<div
														key={messageId}
														className={`flex ${isFromMe ? "justify-end" : "justify-start"}`}
													>
														<div
															className={`max-w-[70%] p-4 rounded-lg shadow-md transition-all duration-500 ${isFromMe
																? isAI
																	? `bg-gradient-to-r from-statusBrandDark to-statusBrand text-white border border-statusBrandLight/30 ${isHighlighted ? "shadow-glow-accent animate-pulse-subtle" : ""}`
																	: isFollowUpMessage(msg)
																		? `bg-gradient-to-r from-statusYellowDark to-statusYellow text-white border border-statusYellowLight/30 ${isHighlighted ? "shadow-glow-yellow animate-pulse-subtle" : ""}`
																		: `bg-chatMessageFromMeBg hover:bg-chatMessageFromMeHover text-white ${isHighlighted ? "shadow-glow-accent animate-pulse-subtle" : ""}`
																: `bg-chatMessageFromOtherBg border border-primaryColor/10 text-white ${isHighlighted ? "shadow-glow-primary animate-pulse-subtle" : ""}`
																}`}
														>
															{isFromMe && (
																<div className={`flex items-center mb-2 text-xs font-medium border-b pb-1 ${
																	isAI
																		? "text-statusBrandLight border-statusBrandLight/30"
																		: isFollowUpMessage(msg)
																			? "text-yellowText border-statusYellowLight/30"
																			: "text-white/70 border-white/20"
																}`}>
																	{isAI && <span className="mr-1">🤖</span>}
																	{isFollowUpMessage(msg) && <Bell size={12} className="mr-1" />}
																	{isAI ? "Assistente IA" : "Você"}
																</div>
															)}

															{selectedChat?.isGroup &&
																!isFromMe &&
																msg.pushName && (
																	<div className="text-xs font-medium text-primaryColor mb-1">
																		{msg.pushName}
																	</div>
																)}
															{messageContent}
															<div
																className={`text-xs mt-2 text-right ${isFromMe
																	? "text-white/70"
																	: "text-textMuted"
																	}`}
															>
																{formatMessageDate(timestamp)}
															</div>
															{msg.deliveryStatus === deliveryStatuses.FAILED && (
																<div className="flex items-center justify-end mt-1 text-xs font-semibold text-red-300">
																	🚫 Não entregue{msg.deliveryErrorCode ? ` (erro ${msg.deliveryErrorCode})` : ""}
																</div>
															)}
														</div>
													</div>
												);
											});

											return messagesWithSeparators;
										})()}
										<div ref={messagesEndRef} />
									</div>
								)}
							</div>

							<div className="p-4 border-t border-emeraldMedium/20 bg-slateContent/80 flex-shrink-0 z-50 relative">
								<form
									onSubmit={handleSendMessage}
									className="flex items-center space-x-2"
								>
									<textarea
										className="w-full bg-slateQuaternary/50 text-white border border-primaryColor/20 rounded-lg px-4 py-3 resize-none focus:outline-none focus:border-primaryColor/50"
										placeholder="Digite uma mensagem..."
										rows="1"
										value={messageInput}
										onChange={(e) => setMessageInput(e.target.value)}
										onKeyDown={handleKeyPress}
										disabled={isSending || !selectedChat}
										ref={messageInputRef}
									/>
									<button
										type="button"
										className={`bg-slateQuaternary/50 text-white border border-primaryColor/20 rounded-lg p-3 transition-all duration-200 ${
											!selectedChat
												? "opacity-50 cursor-not-allowed"
												: "hover:bg-slateQuaternary/70 hover:border-primaryColor/40"
										}`}
										disabled={!selectedChat}
										onClick={() => {
											if (selectedChat) {
												setIsMediaModalOpen(true);
											}
										}}
										title="Anexar arquivo"
									>
										<Paperclip className="h-5 w-5" />
									</button>
									<button
										type="submit"
										className={`bg-gradient-to-r from-primaryDark/80 to-dashboardAccent/80 text-white rounded-lg p-3 transition-all duration-200 ${isSending || !selectedChat || !messageInput.trim()
											? "opacity-50 cursor-not-allowed"
											: "hover:opacity-90"
											}`}
										disabled={
											isSending || !selectedChat || !messageInput.trim()
										}
									>
										{isSending ? (
											<div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
										) : (
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="h-6 w-6"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
												aria-labelledby="sendIconTitle"
											>
												<title id="sendIconTitle">Enviar mensagem</title>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
												/>
											</svg>
										)}
									</button>
								</form>
							</div>
						</>
					) : (
						<div className="flex flex-col items-center justify-center h-full">
							<div className="text-center p-8 rounded-2xl border border-primaryColor/20 max-w-md">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-16 w-16 mx-auto text-primaryLight/70 mb-4"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									aria-labelledby="chatIconTitle"
								>
									<title id="chatIconTitle">Ícone de Chat</title>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
									/>
								</svg>
								<p className="text-lg text-textPrimary mb-2">
									Bem-vindo ao chat do {APP_NAME}
								</p>
								<p className="text-textMuted">
									Selecione um chat para visualizar as mensagens
								</p>
							</div>
						</div>
					)}
				</div>
			</div>
			<MediaSelectionModal
				isOpen={isMediaModalOpen}
				onClose={() => setIsMediaModalOpen(false)}
				chatId={selectedChat?.id}
			/>
		</div>
	);
}
